import { FunctionComponent, useCallback, useState } from 'react'
import {
  Avatar,
  Menu,
  ConexaLogo,
  getTheme,
  useMediaQuery,
  Indicator,
} from '@conexasaude/conexa-hero'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useLocation, useHistory } from 'react-router-dom'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { RenderIf } from '../RenderIf'
import { useNavbar } from './useNavbar'
import useStyles from '@/components/Navbar/styles'
import logo from '@/assets/logo-conexa.svg'
import useBreakpoints from '@/utils/useBreakpoints'
import { NAVBAR_MODALS, NavbarProps } from './types'
import { NOT_ALLOWED_STATUS } from './constants'
import { useAttendanceCallStore } from '@/modules/RealtimeAttendance/store/useAttendanceCall'
import { DependentsModal } from '@/modules/Activation/components/DependentsModal'
import GetDependentData from '@/modules/Activation/mappers/GetDependentData'
import { useModalContext } from '../Modal/contexts/ModalContext'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { useNavigation } from '@/hooks/useNavigation'
import themeServices from '@/theme/theme.services'
import service from '@/modules/Auth/auth-services'
import { User } from '@/modules/Auth/classes/User'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import AvatarWithDependentsAccessWrapper from '../AvatarWithDependentsAccessWrapper'
import { SOSMentalHealth } from '../SOSMentalHealth'
import { useSOSMentalHealth } from '@/hooks/useSOSMentalHealth'
import { DependentSimpleData } from '@/modules/Auth/auth-types'
import { SOSErrorModal } from '../SOSErrorModal'
import { ConfirmPatientModal } from '@/modules/MedicalSpecialities/components/modal/ConfirmPatientModal'
import { MinorAccessRequestModal } from '@/modules/Auth/components/MinorAccessRequestModal'
import { crmEvent } from '@/utils/sendCRMEvent'
import { buildServicesForCRMEvent } from '@/utils/buildServicesForCRMEvent'
import { useChatStore } from '@/modules/Chat/store'
import { Icon } from '@conexasaude/iconography/icons'
import { CHAT_EVENT, CHAT_INTERACTIONS } from '@/modules/Chat/constants'

const Navbar: FunctionComponent<NavbarProps> = ({
  hideMenuOptions = false,
  showAvatar = true,
}) => {
  const theme = getTheme()
  const { embed, setThemeColors } = useThemeGeneratorContext()
  const { user, users, clinic, choiceUser, setUserClinic } = useAuthContext()
  const { pathname } = useLocation()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { isPhone } = useBreakpoints()
  const { states, methods } = useNavbar()
  const history = useHistory()
  const { showModal, closeModal } = useModalContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const [opened, setOpened] = useState(false)
  const navigate = useNavigation()
  const styles = useStyles({
    embed: embed?.value,
    conexaLogo: !clinic?.logos?.show,
  })
  const [updateValueChatStore, hasUnreadMessages] = useChatStore(state => [
    state.updateValue,
    state.hasUnreadMessages,
  ])
  const isMobile = useMediaQuery(`(max-width: ${theme.grid.breakpoint.sm})`)

  const {
    handleRequestImmediateAttendance,
    checkInsuranceAndRedirect,
    immediateAttendanceLoading,
  } = useSOSMentalHealth()

  const attendanceCall = useAttendanceCallStore(store => store.attendanceCall)
  const showTagAvatar = () => {
    if (user?.holder === 'TITULAR') {
      return (
        user?.childrenDependents?.length > 0 ||
        user?.adultDependents?.length > 0
      )
    }
    return user?.holder === 'DEPENDENTE'
  }

  const showInternalAvatarButton = () => {
    if (user?.holder === 'TITULAR') {
      return (
        user?.childrenDependents?.length > 0 ||
        user?.adultDependents?.length > 0
      )
    }
    return user?.holder === 'DEPENDENTE' && user?.isMinorPatient
  }

  const handleAnalytics = useCallback(
    (eventName: string) => {
      if (!eventName) return
      logAnalyticsEvent(eventName)
    },
    [logAnalyticsEvent],
  )

  const handleNavigation = useCallback(
    (
      path: string,
      analyticsEventName: string,
      itemType: string,
      callback?: (...args: unknown[]) => void,
    ) => {
      const hasButtonType = (itemType: string) => /button/.test(itemType)
      handleAnalytics(analyticsEventName)

      if (hasButtonType(itemType) && callback) {
        return callback()
      }

      history.push(path)
    },
    [history, handleAnalytics],
  )

  const handleRedirectToHome = useCallback(() => {
    history.push('/')
  }, [history])

  const isActiveNavItem = useCallback(
    (itemPath: string) => {
      const homePath = '/'

      if (pathname === homePath && itemPath === pathname) return true
      else if (itemPath !== homePath) return pathname.includes(itemPath)
      return false
    },
    [pathname],
  )

  const handleProfileNavigation = useCallback(
    (item: (typeof states.profileAvatarItems)[0]) => {
      handleAnalytics(item.analyticsEvent)
      item.onClick()
    },
    [handleAnalytics, states],
  )

  const profileActions = states.profileAvatarItems
    ?.filter(item => item.show)
    .map(item => ({
      id: item.id,
      label: item.label,
      onClick: !NOT_ALLOWED_STATUS.includes(attendanceCall?.queueStatus)
        ? () => handleProfileNavigation(item)
        : null,
    }))

  const onAvatarCustomHeaderClick = () => {
    if (user?.holder === 'TITULAR') {
      showModal('DEPENDENTS_MODAL_NAVBAR', {
        title: 'Meus dependentes',
        width: 480,
      })
    } else {
      const holder = users?.find(
        holder => holder?.patientId === user?.responsable?.id,
      )
      setUser(holder)
    }
  }

  const setUser = useCallback(
    async (user: User) => {
      try {
        const { token, id, flagAccessedBefore } = user

        const [userClinic, clinicColors, ip] = await Promise.all([
          service.getClinic(token),
          themeServices.getClinicColorsById(user?.clinic?.id),
          service.getUserIP().catch(() => {
            return ''
          }),
        ])

        const { clinic, flags } = userClinic

        if (!flagAccessedBefore) await service.validateFirstAccess(token)

        choiceUser(id, ip)

        setThemeColors(clinicColors)
        setUserClinic(userClinic)
        setSpecialities(clinic)

        if (flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const {
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            } = position.coords
            service.sendGeolocation({
              accuracy,
              altitude,
              altitudeAccuracy,
              heading,
              latitude,
              longitude,
              speed,
            })
          })
        }
        crmEvent('pac_acessou', {
          nome_clinica: user?.clinic?.name,
          id_clinica: user?.clinic?.id,
          id_paciente: user?.id,
          servicos_oferecidos: buildServicesForCRMEvent(userClinic),
        })
        navigate('/')
      } catch (err) {
        showErrorToast(err)
      } finally {
        closeModal()
        location.reload()
      }
    },
    [
      choiceUser,
      setThemeColors,
      setUserClinic,
      setSpecialities,
      navigate,
      closeModal,
    ],
  )

  const toggle = () => {
    setOpened(prevState => !prevState)
  }

  const uniqueById = (dependents: DependentSimpleData[]) => {
    const seenIds = new Set()
    return dependents?.filter(
      ({ idDependente }) =>
        !seenIds.has(idDependente) && seenIds.add(idDependente),
    )
  }
  const adultDependents = uniqueById(user?.adultDependents)
  const childrenDependents = uniqueById(user?.childrenDependents)

  const viewDependents = () => {
    showModal('DEPENDENTS_MODAL', {
      title: 'Meus dependentes',
      width: 480,
    })
  }

  const handleOpenChat = useCallback(() => {
    logAnalyticsEvent(CHAT_EVENT.AT_CHAT_NAVBAR_ACTION, {
      interaction: CHAT_INTERACTIONS.CLICK,
    })
    updateValueChatStore('initChatProvider', true)
    updateValueChatStore('openInboxDrawer', true)
  }, [logAnalyticsEvent, updateValueChatStore])

  return (
    <div>
      <RenderIf condition={!embed.value || embed.navbarEmbed}>
        <Menu.Root maxWidth="xl">
          <Menu.Brand>
            <Menu.Toggle />

            <div
              onClick={handleRedirectToHome}
              css={styles.logoContainer}
              role="button"
            >
              <RenderIf condition={!clinic?.logos.show}>
                <ConexaLogo base="colorful" />
              </RenderIf>
              <RenderIf condition={clinic?.logos.show}>
                <img
                  css={styles.navLogo}
                  src={clinic?.logos.show ? clinic.logos.menu : logo}
                  alt="Logo"
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null
                    currentTarget.src = logo
                  }}
                />
              </RenderIf>
            </div>
          </Menu.Brand>
          <RenderIf condition={!hideMenuOptions}>
            <Menu.List>
              {states.menuItems?.map(item => (
                <RenderIf condition={item?.show} key={item.id}>
                  <Menu.Trigger
                    leftIcon={item?.icon}
                    onClick={() =>
                      handleNavigation(
                        item?.path,
                        item?.analyticsEvent,
                        item?.type,
                        item?.onClick,
                      )
                    }
                    isActive={isActiveNavItem(item.path)}
                  >
                    {item?.label}
                  </Menu.Trigger>
                </RenderIf>
              ))}
            </Menu.List>
          </RenderIf>
          <Menu.Tools>
            <RenderIf
              condition={clinic?.flag?.HabilitarChatPacienteProfissional}
            >
              <Indicator
                offset={{ x: 5, y: 7 }}
                size={isMobile ? 18 : 26}
                show={hasUnreadMessages}
                processing
              >
                <Icon
                  css={styles.chatIcon}
                  size={isMobile ? 25 : 32}
                  name="talk"
                  color={theme.colors.neutral[200]}
                  onClick={handleOpenChat}
                />
              </Indicator>
            </RenderIf>

            <RenderIf condition={showAvatar}>
              <Avatar
                src={user?.photo}
                label={isPhone ? null : user?.shortName}
                customHeaderItem={closeAction => (
                  <AvatarWithDependentsAccessWrapper
                    user={user}
                    showTagAvatar={showTagAvatar}
                    showButtonAvatar={showInternalAvatarButton}
                    onAvatarCustomHeaderClick={onAvatarCustomHeaderClick}
                    toggle={toggle}
                    closeAction={closeAction}
                  />
                )}
                menu
                maxLabelLength={12}
                firstName
                size={isMobile ? 'small' : 'medium'}
                actionListItem={profileActions}
                width={showTagAvatar() ? 310 : 290}
              />
            </RenderIf>
          </Menu.Tools>
        </Menu.Root>
        <RenderIf
          condition={
            opened &&
            (user?.adultDependents?.length > 0 ||
              user?.childrenDependents?.length > 0)
          }
        >
          <DependentsModal
            modalId={NAVBAR_MODALS.DEPENDENTS_MODAL}
            adultDependents={GetDependentData.toDomain(adultDependents)}
            childDependents={GetDependentData.toDomain(childrenDependents)}
          />
          <MinorAccessRequestModal modalId={NAVBAR_MODALS.MINOR_ACCESS_MODAL} />
        </RenderIf>
      </RenderIf>
      <SOSMentalHealth
        isLoading={immediateAttendanceLoading}
        modalId={NAVBAR_MODALS.SOS_MENTAL_HEALTH}
        onRequestImmediateAttendance={handleRequestImmediateAttendance}
      />

      <ConfirmPatientModal
        modalId={NAVBAR_MODALS.SOS_CONFIRM_PATIENT}
        onConfirm={checkInsuranceAndRedirect}
        onNegativeAction={viewDependents}
      />

      <SOSErrorModal
        modalId={NAVBAR_MODALS.SOS_ERROR_MENTAL_HEALTH}
        onOpenSOSModal={methods.handleOpenSOSDialog}
      />
    </div>
  )
}

export default Navbar
